import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Save,
  RotateCcw,
  AlertTriangle,
  Activity,
  BarChart3,
  PieChart,
  MapPin,
  Clock,
  TrendingUp,
  Users,
  Shield,
  Calendar,
  Eye,
  EyeOff,
  GripVertical,
  Settings2,
  Palette
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Widgets disponibles para el dashboard
const AVAILABLE_WIDGETS = [
  { 
    id: 'stats_alertas', 
    nombre: 'Alertas Pendientes', 
    descripcion: 'Contador de alertas por estado',
    icono: 'AlertTriangle',
    categoria: 'estadisticas'
  },
  { 
    id: 'stats_investigacion', 
    nombre: 'En Investigación', 
    descripcion: 'Alertas actualmente en investigación',
    icono: 'Activity',
    categoria: 'estadisticas'
  },
  { 
    id: 'stats_monitores', 
    nombre: 'Monitores Activos', 
    descripcion: 'Cantidad de monitores configurados',
    icono: 'Shield',
    categoria: 'estadisticas'
  },
  { 
    id: 'stats_clientes', 
    nombre: 'Total Clientes', 
    descripcion: 'Clientes en el padrón',
    icono: 'Users',
    categoria: 'estadisticas'
  },
  { 
    id: 'chart_alertas_tiempo', 
    nombre: 'Alertas en el Tiempo', 
    descripcion: 'Gráfico de evolución de alertas',
    icono: 'TrendingUp',
    categoria: 'graficos'
  },
  { 
    id: 'chart_alertas_severidad', 
    nombre: 'Alertas por Severidad', 
    descripcion: 'Distribución por nivel de severidad',
    icono: 'PieChart',
    categoria: 'graficos'
  },
  { 
    id: 'chart_alertas_monitor', 
    nombre: 'Alertas por Monitor', 
    descripcion: 'Top monitores que generan alertas',
    icono: 'BarChart3',
    categoria: 'graficos'
  },
  { 
    id: 'geo_heatmap', 
    nombre: 'Mapa de Calor', 
    descripcion: 'Distribución geográfica de alertas',
    icono: 'MapPin',
    categoria: 'mapas'
  },
  { 
    id: 'alertas_recientes', 
    nombre: 'Alertas Recientes', 
    descripcion: 'Últimas alertas generadas',
    icono: 'Clock',
    categoria: 'listas'
  },
  { 
    id: 'calendario_mini', 
    nombre: 'Calendario Mini', 
    descripcion: 'Vista compacta del calendario',
    icono: 'Calendar',
    categoria: 'otros'
  },
];

const ICON_MAP = {
  AlertTriangle,
  Activity,
  Shield,
  Users,
  TrendingUp,
  PieChart,
  BarChart3,
  MapPin,
  Clock,
  Calendar
};

const CATEGORIAS = {
  estadisticas: { label: 'Estadísticas', color: 'blue' },
  graficos: { label: 'Gráficos', color: 'purple' },
  mapas: { label: 'Mapas', color: 'green' },
  listas: { label: 'Listas', color: 'orange' },
  otros: { label: 'Otros', color: 'slate' }
};

const DashboardConfig = () => {
  const [config, setConfig] = useState({
    widgets_visibles: AVAILABLE_WIDGETS.map(w => w.id),
    orden_widgets: AVAILABLE_WIDGETS.map(w => w.id),
    alertas_mostrar: 5,
    refresh_interval: 60,
    tema_dashboard: 'claro',
    mostrar_bienvenida: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/config/dashboard');
      if (res.data) {
        setConfig(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/config/dashboard', config);
      toast.success('Configuración guardada');
      setHasChanges(false);
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({
      widgets_visibles: AVAILABLE_WIDGETS.map(w => w.id),
      orden_widgets: AVAILABLE_WIDGETS.map(w => w.id),
      alertas_mostrar: 5,
      refresh_interval: 60,
      tema_dashboard: 'claro',
      mostrar_bienvenida: true
    });
    setHasChanges(true);
  };

  const toggleWidget = (widgetId) => {
    setConfig(prev => {
      const isVisible = prev.widgets_visibles.includes(widgetId);
      return {
        ...prev,
        widgets_visibles: isVisible
          ? prev.widgets_visibles.filter(id => id !== widgetId)
          : [...prev.widgets_visibles, widgetId]
      };
    });
    setHasChanges(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = [...config.orden_widgets];
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);

    setConfig(prev => ({ ...prev, orden_widgets: items }));
    setHasChanges(true);
  };

  const getOrderedWidgets = () => {
    return config.orden_widgets
      .map(id => AVAILABLE_WIDGETS.find(w => w.id === id))
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <LayoutDashboard className="text-blue-600" />
            Configuración del Dashboard
          </h1>
          <p className="text-slate-600 mt-1">
            Personaliza qué información se muestra en el dashboard principal
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges}
          >
            <RotateCcw size={16} className="mr-2" />
            Restaurar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={{ backgroundColor: '#2563eb' }}
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Widgets Configuration */}
        <div className="lg:col-span-2">
          <Card className="bg-white p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings2 size={20} />
              Widgets del Dashboard
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Arrastra para reordenar. Click en el ojo para mostrar/ocultar.
            </p>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="widgets">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-2"
                  >
                    {getOrderedWidgets().map((widget, index) => {
                      const Icon = ICON_MAP[widget.icono] || LayoutDashboard;
                      const isVisible = config.widgets_visibles.includes(widget.id);
                      const categoria = CATEGORIAS[widget.categoria];

                      return (
                        <Draggable key={widget.id} draggableId={widget.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                snapshot.isDragging 
                                  ? 'bg-blue-50 border-blue-200 shadow-lg' 
                                  : isVisible 
                                    ? 'bg-white border-slate-200' 
                                    : 'bg-slate-50 border-slate-200 opacity-60'
                              }`}
                            >
                              <div {...provided.dragHandleProps} className="cursor-grab">
                                <GripVertical size={18} className="text-slate-400" />
                              </div>
                              
                              <div className={`p-2 rounded-lg bg-${categoria.color}-100`}>
                                <Icon size={18} className={`text-${categoria.color}-600`} />
                              </div>
                              
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{widget.nombre}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded bg-${categoria.color}-100 text-${categoria.color}-700`}>
                                    {categoria.label}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{widget.descripcion}</p>
                              </div>
                              
                              <button
                                onClick={() => toggleWidget(widget.id)}
                                className={`p-2 rounded-lg transition-colors ${
                                  isVisible 
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                    : 'bg-slate-200 text-slate-400 hover:bg-slate-300'
                                }`}
                              >
                                {isVisible ? <Eye size={18} /> : <EyeOff size={18} />}
                              </button>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Card>
        </div>

        {/* General Settings */}
        <div className="space-y-4">
          <Card className="bg-white p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Palette size={20} />
              Opciones Generales
            </h2>

            <div className="space-y-4">
              <div>
                <Label className="text-sm">Alertas recientes a mostrar</Label>
                <Select 
                  value={String(config.alertas_mostrar)} 
                  onValueChange={(v) => {
                    setConfig(prev => ({ ...prev, alertas_mostrar: parseInt(v) }));
                    setHasChanges(true);
                  }}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 alertas</SelectItem>
                    <SelectItem value="5">5 alertas</SelectItem>
                    <SelectItem value="10">10 alertas</SelectItem>
                    <SelectItem value="15">15 alertas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm">Intervalo de actualización</Label>
                <Select 
                  value={String(config.refresh_interval)} 
                  onValueChange={(v) => {
                    setConfig(prev => ({ ...prev, refresh_interval: parseInt(v) }));
                    setHasChanges(true);
                  }}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 segundos</SelectItem>
                    <SelectItem value="60">1 minuto</SelectItem>
                    <SelectItem value="120">2 minutos</SelectItem>
                    <SelectItem value="300">5 minutos</SelectItem>
                    <SelectItem value="0">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <Label className="text-sm">Mostrar bienvenida</Label>
                  <p className="text-xs text-slate-500">Mensaje de bienvenida con nombre</p>
                </div>
                <Switch
                  checked={config.mostrar_bienvenida}
                  onCheckedChange={(v) => {
                    setConfig(prev => ({ ...prev, mostrar_bienvenida: v }));
                    setHasChanges(true);
                  }}
                />
              </div>
            </div>
          </Card>

          {/* Preview Card */}
          <Card className="bg-white p-4">
            <h2 className="text-lg font-semibold mb-4">Vista Previa</h2>
            <div className="text-sm text-slate-600">
              <p><strong>Widgets activos:</strong> {config.widgets_visibles.length} de {AVAILABLE_WIDGETS.length}</p>
              <p className="mt-2"><strong>Orden:</strong></p>
              <ol className="list-decimal list-inside mt-1 text-xs space-y-1">
                {config.widgets_visibles.slice(0, 5).map((id, idx) => {
                  const widget = AVAILABLE_WIDGETS.find(w => w.id === id);
                  return widget ? <li key={id}>{widget.nombre}</li> : null;
                })}
                {config.widgets_visibles.length > 5 && (
                  <li className="text-slate-400">...y {config.widgets_visibles.length - 5} más</li>
                )}
              </ol>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardConfig;
